/**
 * Genre Insights data layer.
 *
 * Talks to GET /titles/genres, which answers one product question:
 * "which genres grow your subscriber base (acquisition) vs keep it (retention),
 * and is that split changing?"
 *
 * The endpoint returns both halves of the macro→micro view in a single call:
 *   - macro: every genre's acquired-vs-retained totals for one quarter (+ filters),
 *     already sorted by total revenue desc, each genre carrying a clean `label`.
 *   - micro: the selected genre's acquired-vs-retained over the last N quarters.
 * Each bar carries all three measures (revenue / subscribers / titles) so the UI
 * can toggle without refetching.
 *
 * NOTE on `titles`: the backend only populates `total.titles`; the acquired/retained
 * split for titles is 0 (a title can't be cleanly attributed to acq vs ret). So under
 * the Titles measure the stack reads as a single total segment — revenue and
 * subscribers are the measures that split meaningfully.
 */
import { apiUrl, getJson } from './api';

/** The three interchangeable measures every bar carries. */
export type GenreMeasure = 'revenue' | 'subscribers' | 'titles';

/** One bar's worth of numbers — the same shape for acquired, retained and total. */
export interface GenreMeasures {
  revenue: number;
  subscribers: number;
  titles: number;
}

/** A genre's acquired-vs-retained split for one quarter (macro row). */
export interface GenreMacroRow {
  genre: string; // lowercase slug (the stable key)
  label: string; // clean display name from the backend (_genre_label)
  acquired: GenreMeasures;
  retained: GenreMeasures;
  total: GenreMeasures;
}

/** One quarter's acquired-vs-retained split for the selected genre (micro row). */
export interface GenreMicroRow {
  quarter: string;
  endDate: string;
  acquired: GenreMeasures;
  retained: GenreMeasures;
  total: GenreMeasures;
}

export interface GenresMacro {
  quarter: string;
  endDate: string;
  genres: GenreMacroRow[];
}

export interface GenresMicro {
  genre: string;
  label: string;
  series: GenreMicroRow[];
}

export interface GenresResponse {
  macro: GenresMacro;
  micro: GenresMicro;
  meta: {
    genre: string;
    platform: string;
    region: string;
    endDate: string;
    lastQuarters: number;
    measures: GenreMeasure[];
  };
}

export interface GenresParams {
  /** The micro subject. Backend defaults to 'crime'. */
  genre: string;
  /** '' means all platforms (filter omitted). */
  platform: string;
  /** '' means all regions (filter omitted). */
  region: string;
  /** '' means latest quarter (backend defaults to it). */
  endDate: string;
  lastQuarters?: number;
}

export async function fetchGenres(params: GenresParams): Promise<GenresResponse> {
  const search = new URLSearchParams();
  if (params.genre) search.set('genre', params.genre);
  if (params.platform) search.set('platform', params.platform);
  if (params.region) search.set('region', params.region);
  if (params.endDate) search.set('endDate', params.endDate);
  if (params.lastQuarters) search.set('lastQuarters', String(params.lastQuarters));
  return getJson<GenresResponse>(apiUrl(`/titles/genres?${search}`));
}

/** Human label + short label for each measure, kept beside the type so the UI stays declarative. */
export const GENRE_MEASURE_META: Record<GenreMeasure, { label: string; short: string }> = {
  revenue: { label: 'Revenue', short: 'Revenue' },
  subscribers: { label: 'Subscriber Equivalent', short: 'Subs Equiv.' },
  titles: { label: 'Titles', short: 'Titles' },
};

/**
 * Retained as a share of total, for the headline "X% of its revenue from retention"
 * takeaway. Returns null when total is zero so the caller renders — rather than 0%.
 */
export function retainedShare(
  row: { retained: GenreMeasures; total: GenreMeasures },
  measure: GenreMeasure,
): number | null {
  const total = row.total[measure];
  if (!total) return null;
  return row.retained[measure] / total;
}

/** Acquired as a share of total (the complement used in takeaways). */
export function acquiredShare(
  row: { acquired: GenreMeasures; total: GenreMeasures },
  measure: GenreMeasure,
): number | null {
  const total = row.total[measure];
  if (!total) return null;
  return row.acquired[measure] / total;
}

// ─── v2 insight engine ───────────────────────────────────────────────────────
// Pure functions over the whole macro array (every genre) and the micro series
// (one genre over time). Each returns a finding or null. The page renders 2–3 of
// these as the insight banner, replacing v1's single restated percentage.

/** A computed finding for the insight banner. `kind` drives the accent colour. */
export interface GenreInsight {
  kind: 'concentration' | 'uniform' | 'mover';
  /** Short headline, already measure-aware. HTML-free; the UI bolds the subject. */
  subject: string; // the genre label (or '' when the finding isn't about one genre)
  text: string; // the sentence after the subject
}

/** Mean of a numeric list, or null when empty. */
function mean(xs: number[]): number | null {
  if (xs.length === 0) return null;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

/** Population standard deviation of a numeric list, or null when empty. */
function stdev(xs: number[]): number | null {
  const m = mean(xs);
  if (m == null) return null;
  return Math.sqrt(mean(xs.map((x) => (x - m) ** 2)) ?? 0);
}

/** Median of a numeric list, or null when empty. */
export function median(xs: number[]): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Only genres whose total for this measure is non-zero. */
function nonZero(genres: GenreMacroRow[], measure: GenreMeasure): GenreMacroRow[] {
  return genres.filter((g) => g.total[measure] > 0);
}

/**
 * Finding 1 — CONCENTRATION. The data's dominant signal is that revenue is wildly
 * top-heavy (one genre ~45%, top-5 ~77%). This finding names the leader and how much
 * of the total it + the runners-up command.
 */
export function concentrationInsight(
  genres: GenreMacroRow[],
  measure: GenreMeasure,
): GenreInsight | null {
  const rows = nonZero(genres, measure);
  if (rows.length < 5) return null;
  const sorted = [...rows].sort((a, b) => b.total[measure] - a.total[measure]);
  const total = sorted.reduce((a, g) => a + g.total[measure], 0);
  if (!total) return null;
  const leader = sorted[0];
  const leaderShare = leader.total[measure] / total;
  const top5Share = sorted.slice(0, 5).reduce((a, g) => a + g.total[measure], 0) / total;
  return {
    kind: 'concentration',
    subject: leader.label,
    text: `dominates — ${pct(leaderShare)} of all genre ${measureNoun(
      measure,
    )}. The top 5 genres hold ${pct(top5Share)}; the rest is a long tail.`,
  };
}

/**
 * Finding 2 — UNIFORM SPLIT. The honest headline the quadrant tried and failed to
 * show: acquire-vs-retain barely varies between genres. We quantify the spread
 * (stdev in percentage points) and state the band. Null under Titles (no split).
 */
export function uniformSplitInsight(
  genres: GenreMacroRow[],
  measure: GenreMeasure,
): GenreInsight | null {
  if (measure === 'titles') return null;
  const rows = nonZero(genres, measure);
  if (rows.length < 5) return null;
  const retShares = rows.map((g) => g.retained[measure] / g.total[measure]);
  const sd = stdev(retShares);
  if (sd == null) return null;
  const lo = Math.min(...retShares);
  const hi = Math.max(...retShares);
  return {
    kind: 'uniform',
    subject: 'Genre',
    text: `barely changes the acquire-vs-retain balance — every genre keeps ${pct(lo)}–${pct(
      hi,
    )} of its ${measureNoun(measure)} from retention (±${(sd * 100).toFixed(1)}pts). The real differences are size and growth.`,
  };
}

/**
 * Finding 3 — the MOVER. The real movement is in revenue SIZE over time, not the
 * near-constant split. Reports the selected genre's growth across the micro window.
 * Threshold raised to a meaningful ±10% so noise doesn't surface as a finding.
 */
export function moverInsight(micro: GenresMicro, measure: GenreMeasure): GenreInsight | null {
  const series = micro.series;
  if (series.length < 2) return null;
  const first = series[0];
  const last = series[series.length - 1];
  const fv = first.total[measure];
  const lv = last.total[measure];
  if (!fv || !lv) return null;
  const change = lv / fv - 1;
  if (Math.abs(change) < 0.1) return null; // < 10% over the window = not worth a headline
  const dir = change >= 0 ? 'grew' : 'shrank';
  const span = series.length;
  return {
    kind: 'mover',
    subject: micro.label,
    text: `${dir} ${Math.abs(change * 100).toFixed(0)}% in ${measureNoun(
      measure,
    )} over ${span} quarters (${quarterShort(first.endDate)}→${quarterShort(last.endDate)}) — momentum lives here, not in the mix.`,
  };
}

/** Build the findings the banner shows, in priority order, dropping nulls. */
export function buildInsights(
  macro: GenresMacro,
  micro: GenresMicro,
  measure: GenreMeasure,
): GenreInsight[] {
  return [
    concentrationInsight(macro.genres, measure),
    uniformSplitInsight(macro.genres, measure),
    moverInsight(micro, measure),
  ].filter((x): x is GenreInsight => x !== null);
}

// ─── Concentration (Pareto) data ─────────────────────────────────────────────
// The hero view. Genres ranked by total[measure] desc, each carrying its acq/ret
// split (for the within-bar stack) plus the running cumulative share (for the
// Pareto line that shows how few genres make up most of the revenue).

export interface ParetoDatum {
  genre: string;
  label: string;
  acquired: number;
  retained: number;
  total: number;
  share: number; // this genre's share of the measure total (0..1)
  cumulative: number; // running cumulative share through this genre (0..1)
}

/** Build the ranked Pareto rows. Empty when nothing carries the measure. */
export function buildPareto(genres: GenreMacroRow[], measure: GenreMeasure): ParetoDatum[] {
  const rows = nonZero(genres, measure).sort((a, b) => b.total[measure] - a.total[measure]);
  const grand = rows.reduce((a, g) => a + g.total[measure], 0);
  if (!grand) return [];
  let run = 0;
  return rows.map((g) => {
    const total = g.total[measure];
    run += total;
    return {
      genre: g.genre,
      label: g.label,
      acquired: g.acquired[measure],
      retained: g.retained[measure],
      total,
      share: total / grand,
      cumulative: run / grand,
    };
  });
}

// ─── Momentum data ───────────────────────────────────────────────────────────
// The selected genre's total[measure] over the micro window, as a simple series the
// UI renders as an area/line — answering "is this genre growing or shrinking?".

export interface MomentumDatum {
  quarter: string;
  endDate: string;
  total: number;
  acquired: number;
  retained: number;
}

export function buildMomentum(micro: GenresMicro, measure: GenreMeasure): MomentumDatum[] {
  return micro.series.map((r) => ({
    quarter: r.quarter,
    endDate: r.endDate,
    total: r.total[measure],
    acquired: r.acquired[measure],
    retained: r.retained[measure],
  }));
}

// ─── small formatting helpers (kept local to the data layer) ─────────────────
function pct(x: number): string {
  return `${(x * 100).toFixed(0)}%`;
}

function measureNoun(measure: GenreMeasure): string {
  return measure === 'revenue' ? 'revenue' : measure === 'subscribers' ? 'subscriber base' : 'titles';
}

/** "2026 Q1" from an endDate — reused in mover text. Kept tiny + local. */
function quarterShort(endDate: string): string {
  const d = new Date(endDate);
  if (Number.isNaN(d.getTime())) return endDate;
  const q = Math.floor(d.getUTCMonth() / 3) + 1;
  return `${d.getUTCFullYear()} Q${q}`;
}
